<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <h2 class="title">EFD Sales</h2>
        <app-input
          @input.native="onEFDSearch"
          v-model="filter_search"
          class="input--200"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_legal_entity"
          :options="legalEntities"
          label="short_name"
          placeholder="Legal entity"
          class="select--200"
        />
      </app-cells>
      <v-client-table
        :data="table_efd.items"
        :columns="table_efd.columns"
        :options="table_efd.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader_efd }"
      >
        <template #timestamp="{ row }">{{ dateTimeFormat(row.timestamp) }}</template>
        <template #verification_code="{ row }">
          <button
            v-if="row.verification_link"
            @click="onCopyToClipboard(row.verification_link)"
            class="table-default__copy"
          >
            {{ row.verification_code }}
          </button>
          <template v-else>{{ row.verification_code || '-' }}</template>
        </template>
        <template #source="{ row }">{{ row.source.name }} (#{{ row.source_instance_id }})</template>
        <template #legal_entity="{ row }">{{ row.legal_entity?.short_name || '-' }}</template>
        <template #vfd_z_number="{ row }">{{ row.vfd_z_number }}</template>
        <template #amount_total="{ row }">{{ amountFormat(row.amount_total) }} TSh</template>
        <template #amount_vat_exc="{ row }">{{ amountFormat(row.amount_vat_exc) }} TSh</template>
        <template #amount_vat="{ row }">{{ amountFormat(row.amount_vat) }} TSh</template>
        <template #amount_exempted="{ row }">{{ amountFormat(row.amount_exempted) }} TSh</template>
        <template #customer_name="{ row }">{{ row.customer_name || '-' }}</template>
        <template #customer_id="{ row }">{{ row.customer_id || '-' }}</template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationEFD"
            v-if="pagination_efd.pages > 1"
            v-model="pagination_efd.page"
            :page-count="pagination_efd.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_efd.count" :count="table_efd.items.length" :page="pagination_efd.page" />
      </app-cells>
      <app-cells>
        <h2 class="title">Z-Reports</h2>
        <div class="date-picker-wrapper">
          <VueCtkDateTimePicker
            id="date_picker_balance"
            class="custom-date-time-picker custom-date-time-picker--range"
            v-model="filter_dates"
            :custom-shortcuts="custom_shortcuts"
            @is-hidden="onIsHiddenPicker"
            format="YYYY-MM-DD"
            formatted="ll"
            label="Select dates"
            color="#77b017"
            button-color="#77b017"
            overlay
            no-header
            no-label
            locale="en"
            range
          />
          <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
        </div>
      </app-cells>
      <v-client-table
        :data="table_reports.items"
        :columns="table_reports.columns"
        :options="table_reports.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader_reports }"
      >
        <template #h__vata><div class="table-default__left">VAT [A]</div></template>
        <template #h__vate><div class="table-default__left">VAT [E]</div></template>
        <template #h__total><div class="table-default__left">Total</div></template>
        <template #z_number="{ row }">{{ row.z_number || '-' }}</template>
        <template #legal_entity="{ row }">{{ row.legal_entity?.short_name || '-' }}</template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #receipts="{ row }">{{ dateTimeFormat(row.first_receipt) }}<br>{{ dateTimeFormat(row.last_receipt) }}</template>
        <template #vata="{ row }">
          <div class="table-default__left">
            TURNOVER: {{ amountFormat(row.vat_a_turnover) }}<br>
            NET: {{ amountFormat(row.vat_a_net) }}<br>
            TAX: {{ amountFormat(row.vat_a_tax) }}
          </div>
        </template>
        <template #vate="{ row }">
          <div class="table-default__left">
            TURNOVER: {{ amountFormat(row.vat_e_turnover) }}<br>
            NET: {{ amountFormat(row.vat_e_net) }}<br>
            TAX: {{ amountFormat(row.vat_e_tax) }}
          </div>
        </template>
        <template #total="{ row }">
          <div class="table-default__left">
            TURNOVER: {{ amountFormat(row.total_turnover) }}<br>
            NET: {{ amountFormat(row.total_net) }}<br>
            TAX: {{ amountFormat(row.total_tax) }}
          </div>
        </template>
        <template #net="{ row }">{{ amountFormat(row.net_sale) }}</template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationReports"
            v-if="pagination_reports.pages > 1"
            v-model="pagination_reports.page"
            :page-count="pagination_reports.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_reports.count" :count="table_reports.items.length" :page="pagination_reports.page" />
      </app-cells>
      <app-cells>
        <h2 class="title">Z-Reports Summary</h2>
      </app-cells>
      <v-client-table
        :data="table_summary.items"
        :columns="table_summary.columns"
        :options="table_summary.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader_summary }"
      >
        <template #h__vata><div class="table-default__left">VAT [A]</div></template>
        <template #h__vate><div class="table-default__left">VAT [E]</div></template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #legal_entity="{ row }">{{ row.legal_entity?.short_name || '-' }}</template>
        <template #period="{ row }">{{ dateFormat(row.date_from) }}<br>{{ dateFormat(row.date_to) }}</template>
        <template #receipts="{ row }">{{ dateTimeFormat(row.first_receipt) }}<br>{{ dateTimeFormat(row.last_receipt) }}</template>
        <template #vata="{ row }">
          <div class="table-default__left">
            TURNOVER: {{ amountFormat(row.vat_a_turnover) }}<br>
            NET: {{ amountFormat(row.vat_a_net) }}<br>
            TAX: {{ amountFormat(row.vat_a_tax) }}
          </div>
        </template>
        <template #vate="{ row }">
          <div class="table-default__left">
            TURNOVER: {{ amountFormat(row.vat_e_turnover) }}<br>
            NET: {{ amountFormat(row.vat_e_net) }}<br>
            TAX: {{ amountFormat(row.vat_e_tax) }}
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationSummary"
            v-if="pagination_summary.pages > 1"
            v-model="pagination_summary.page"
            :page-count="pagination_summary.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_summary.count" :count="table_summary.items.length" :page="pagination_summary.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import {
  getFinanceEFDReceipts,
  getFinanceEFDReceiptsZReports,
  getFinanceEFDReceiptsZReportsSummary,
} from '@/http'
import debounce from 'lodash/debounce'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import moment from 'moment'

export default {
  name: 'FinanceEFDReceipts',
  props: ['legalEntities'],
  components: { VueCtkDateTimePicker },
  data() {
    return {
      page_loaded: false,
      date_picker_error: false,
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      filter_dates: null,
      filter_search: '',
      filter_search_invoice: '',
      filter_legal_entity: null,
      table_preloader_efd: false,
      table_preloader_reports: false,
      table_preloader_summary: false,
      table_efd: {
        items: [],
        columns: [
          'timestamp',
          'legal_entity',
          'verification_code',
          'source',
          'vfd_z_number',
          'amount_total',
          'amount_vat_exc',
          'amount_vat',
          'amount_exempted',
          'customer_name',
          'customer_id',
        ],
        options: {
          headings: {
            'timestamp': 'Date',
            'legal_entity': 'Legal entity',
            'verification_code': 'Verification code',
            'source': 'Source',
            'vfd_z_number': 'Z Number',
            'amount_total': 'Total',
            'amount_vat_exc': 'Excluded VAT',
            'amount_vat': 'VAT',
            'amount_exempted': 'Exempted',
            'customer_name': 'Customer name',
            'customer_id': 'Customer ID',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_efd: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_reports: {
        items: [],
        columns: [
          'z_number',
          'legal_entity',
          'date',
          'receipts',
          'vata',
          'vate',
          'total',
          'net',
        ],
        options: {
          headings: {
            'z_number': 'Z',
            'legal_entity': 'Legal entity',
            'date': 'Date',
            'receipts': 'Receipts',
            'vata': 'VAT [A]',
            'vate': 'VAT [E]',
            'total': 'Total',
            'net': 'NET',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_reports: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_summary: {
        items: [],
        columns: [
          'date',
          'legal_entity',
          'period',
          'receipts',
          'vata',
          'vate',
        ],
        options: {
          headings: {
            'date': 'Date',
            'legal_entity': 'Legal entity',
            'period': 'Period',
            'receipts': 'Receipts',
            'vata': 'VAT [A]',
            'vate': 'VAT [E]',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 24,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_summary: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  created() {
    Promise.all([
      this.fetchItemsEFD(),
      this.fetchItemsReports(),
      this.fetchItemsSummary()
    ])
      .then(response => {
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsEFD() {
      this.table_preloader_efd = true
      const response = await getFinanceEFDReceipts({
        page: this.pagination_efd.page,
        page_size: 20,
        search: this.filter_search,
        legal_entity: this.filter_search ? '' : this.filter_legal_entity?.code
      })
      this.table_efd.items = response.data.results
      this.pagination_efd.pages = response.data.pages
      this.pagination_efd.count = response.data.count
      this.table_preloader_efd = false
    },
    async fetchItemsReports() {
      this.table_preloader_reports = true
      const response = await getFinanceEFDReceiptsZReports({
        page: this.pagination_reports.page,
        page_size: 20,
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end,
      })
      this.table_reports.items = response.data.results
      this.pagination_reports.pages = response.data.pages
      this.pagination_reports.count = response.data.count
      this.table_preloader_reports = false
    },
    async fetchItemsSummary() {
      this.table_preloader_summary = true
      const response = await getFinanceEFDReceiptsZReportsSummary({
        page: this.pagination_summary.page,
        page_size: 24,
      })
      this.table_summary.items = response.data.results
      this.pagination_summary.pages = response.data.pages
      this.pagination_summary.count = response.data.count
      this.table_preloader_summary = false
    },
    onClickPaginationEFD(page) {
      this.pagination_efd.page = page
      this.fetchItemsEFD()
    },
    onClickPaginationReports(page) {
      this.pagination_reports.page = page
      this.fetchItemsReports()
    },
    onClickPaginationSummary(page) {
      this.pagination_summary.page = page
      this.fetchItemsSummary()
    },
    onEFDSearch() {
      this.searchEFD(this)
    },
    searchEFD: debounce(vm => {
      vm.pagination_efd.page = 1
      vm.fetchItemsEFD()
    }, 350),
    onChangeFilter() {
      this.filter_search = ''
      this.pagination_efd.page = 1
      this.fetchItemsEFD()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchItemsReports()
      } else this.date_picker_error = true
    },
    onCopyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>
